import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Upload, Loader2, Star } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = {
  name: "", gender: "women", category: "bikini", price: 0,
  tagline: "", description: "", fit_note: "", featured: false,
  colors: [], fabrics: [], images: [],
};

const CATEGORIES = ["bikini", "one-piece", "trunks"];

export const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);
  const [editingSlug, setEditingSlug] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm({ ...EMPTY }); setEditingSlug(null); };
  const startEdit = (p) => {
    setForm({
      name: p.name, gender: p.gender, category: p.category, price: p.price,
      tagline: p.tagline, description: p.description, fit_note: p.fit_note,
      featured: p.featured, colors: p.colors || [], fabrics: p.fabrics || [], images: p.images || [],
    });
    setEditingSlug(p.slug);
  };
  const close = () => { setForm(null); setEditingSlug(null); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (files) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((prev) => ({ ...prev, images: [...prev.images, data.url] }));
      }
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name || !form.price || form.images.length === 0) {
      toast.error("Name, price and at least one image are required");
      return;
    }
    setSaving(true);
    try {
      if (editingSlug) await api.put(`/admin/products/${editingSlug}`, form);
      else await api.post("/admin/products", form);
      toast.success(editingSlug ? "Model updated" : "Model created");
      await load();
      close();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this model?")) return;
    try {
      await api.delete(`/admin/products/${slug}`);
      toast.success("Model deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const inputCls = "w-full bg-white border border-charcoal/20 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-charcoal";

  return (
    <div data-testid="product-manager">
      {!form && (
        <>
          <div className="flex justify-between items-center mb-6">
            <p className="text-sm text-charcoal/60">{products.length} models</p>
            <button data-testid="new-model-btn" onClick={startNew} className="inline-flex items-center gap-2 bg-ocean text-sand px-5 py-3 rounded-full overline hover:opacity-90 transition-opacity">
              <Plus size={16} /> New model
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p) => (
              <div key={p.slug} data-testid={`pm-card-${p.slug}`} className="bg-white border border-charcoal/10 rounded-2xl overflow-hidden">
                <div className="aspect-[4/3] bg-linen overflow-hidden">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-lg">{p.name}</h3>
                    {p.featured && <Star size={14} className="text-sunkissed fill-sunkissed" />}
                  </div>
                  <p className="text-xs text-charcoal/50 capitalize">{p.gender} · {p.category} · CHF {p.price}</p>
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`pm-edit-${p.slug}`} onClick={() => startEdit(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-charcoal/25 rounded-lg py-2 text-xs hover:border-charcoal transition-colors">
                      <Pencil size={13} /> Edit
                    </button>
                    <button data-testid={`pm-delete-${p.slug}`} onClick={() => remove(p.slug)} className="inline-flex items-center justify-center border border-charcoal/25 rounded-lg py-2 px-3 text-xs hover:border-destructive hover:text-destructive transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {form && (
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6 md:p-8" data-testid="product-form">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-serif text-2xl">{editingSlug ? "Edit model" : "New model"}</h3>
            <button onClick={close} data-testid="pm-close" className="text-charcoal/50 hover:text-charcoal"><X size={22} /></button>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="overline text-charcoal/55">Name</label>
              <input data-testid="pm-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <label className="overline text-charcoal/55">Price (CHF)</label>
              <input data-testid="pm-price" type="number" className={inputCls} value={form.price} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <label className="overline text-charcoal/55">Gender</label>
              <select data-testid="pm-gender" className={inputCls} value={form.gender} onChange={(e) => set("gender", e.target.value)}>
                <option value="women">Women</option>
                <option value="men">Men</option>
              </select>
            </div>
            <div>
              <label className="overline text-charcoal/55">Category</label>
              <select data-testid="pm-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="overline text-charcoal/55">Tagline</label>
              <input data-testid="pm-tagline" className={inputCls} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="overline text-charcoal/55">Description</label>
              <textarea data-testid="pm-description" rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="overline text-charcoal/55">Fit note</label>
              <input data-testid="pm-fitnote" className={inputCls} value={form.fit_note} onChange={(e) => set("fit_note", e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-sm md:col-span-2">
              <input type="checkbox" data-testid="pm-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} />
              Feature on homepage
            </label>
          </div>

          {/* Colors */}
          <Repeater
            title="Colours" testid="colors" items={form.colors}
            onAdd={() => set("colors", [...form.colors, { name: "", hex: "#C8A56A" }])}
            onRemove={(i) => set("colors", form.colors.filter((_, x) => x !== i))}
            render={(c, i) => (
              <>
                <input placeholder="Name" className={inputCls} value={c.name} onChange={(e) => { const a = [...form.colors]; a[i] = { ...a[i], name: e.target.value }; set("colors", a); }} />
                <input type="color" className="h-11 w-14 rounded-lg border border-charcoal/20" value={c.hex} onChange={(e) => { const a = [...form.colors]; a[i] = { ...a[i], hex: e.target.value }; set("colors", a); }} />
              </>
            )}
          />

          {/* Fabrics */}
          <Repeater
            title="Fabrics" testid="fabrics" items={form.fabrics}
            onAdd={() => set("fabrics", [...form.fabrics, { name: "", note: "" }])}
            onRemove={(i) => set("fabrics", form.fabrics.filter((_, x) => x !== i))}
            render={(f, i) => (
              <>
                <input placeholder="Name" className={inputCls} value={f.name} onChange={(e) => { const a = [...form.fabrics]; a[i] = { ...a[i], name: e.target.value }; set("fabrics", a); }} />
                <input placeholder="Note" className={inputCls} value={f.note} onChange={(e) => { const a = [...form.fabrics]; a[i] = { ...a[i], note: e.target.value }; set("fabrics", a); }} />
              </>
            )}
          />

          {/* Images */}
          <div className="mt-7">
            <div className="overline text-charcoal/55 mb-3">Image gallery</div>
            <div
              data-testid="pm-gallery-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
              onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files?.length) onUpload(e.dataTransfer.files); }}
              className={`flex flex-wrap gap-3 rounded-2xl transition-colors ${dragging ? "ring-2 ring-sunkissed/50 bg-linen/50 p-3" : "p-0"}`}
            >
              {form.images.map((src, i) => (
                <div key={i} className="relative w-24 h-32 rounded-2xl overflow-hidden border border-charcoal/15" data-testid={`pm-image-${i}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => set("images", form.images.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-charcoal/70 text-white rounded-full p-1">
                    <X size={12} />
                  </button>
                </div>
              ))}
              <button
                data-testid="pm-upload-btn"
                onClick={() => fileRef.current?.click()}
                className="w-24 h-32 rounded-2xl border border-dashed border-charcoal/30 flex flex-col items-center justify-center text-charcoal/50 hover:border-charcoal transition-colors"
              >
                {uploading ? <Loader2 size={20} className="animate-spin" /> : <><Upload size={20} /><span className="text-[10px] mt-1 text-center leading-tight px-1">Drag & drop<br/>or click</span></>}
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onUpload(e.target.files)} />
            </div>
          </div>

          <div className="flex gap-3 mt-8">
            <button data-testid="pm-save" disabled={saving} onClick={save} className="bg-ocean text-sand px-8 py-3.5 rounded-full overline hover:opacity-90 transition-opacity disabled:opacity-50">
              {saving ? "Saving…" : editingSlug ? "Save changes" : "Create model"}
            </button>
            <button onClick={close} className="border border-charcoal/25 px-8 py-3.5 rounded-full overline hover:border-charcoal transition-colors">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Repeater = ({ title, testid, items, onAdd, onRemove, render }) => (
  <div className="mt-7">
    <div className="flex items-center justify-between mb-3">
      <div className="overline text-charcoal/55">{title}</div>
      <button data-testid={`pm-add-${testid}`} onClick={onAdd} className="inline-flex items-center gap-1.5 text-xs text-sunkissed hover:text-charcoal transition-colors">
        <Plus size={13} /> Add
      </button>
    </div>
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`pm-${testid}-${i}`}>
          {render(it, i)}
          <button onClick={() => onRemove(i)} className="text-charcoal/40 hover:text-destructive p-2"><Trash2 size={14} /></button>
        </div>
      ))}
      {items.length === 0 && <p className="text-xs text-charcoal/40">None yet.</p>}
    </div>
  </div>
);
